import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@personic/shared'

interface Attestation {
  id: string
  profile_id: string
  canonical_id: string
  regulated_domain: string
  status: string
  submitted_by: string
  submitted_at: string
  reviewed_at: string | null
  reviewer_name: string | null
  expiration_date: string | null
}

interface TenantAttestationsCardProps {
  tenantId: string
}

export default function TenantAttestationsCard({ tenantId }: TenantAttestationsCardProps) {
  const [statusFilter, setStatusFilter] = useState<string>('all')

  const { data, isLoading, error } = useQuery<Attestation[]>({
    queryKey: ['tenant-attestations', tenantId, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams()
      params.append('tenant_id', tenantId)
      if (statusFilter !== 'all') {
        params.append('status', statusFilter)
      }

      const res = await api.get(`/admin/attestations?${params.toString()}`)
      return res.data
    },
  })

  if (isLoading) {
    return (
      <div className="card p-6">
        <div className="text-gray-500">Loading attestations...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="card p-6 bg-red-50 border-red-200">
        <p className="text-red-700">Failed to load attestations.</p>
      </div>
    )
  }

  const attestations = data || []

  return (
    <div className="card">
      <div className="border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            Regulated Signal Attestations ({attestations.length})
          </h3>

          {/* Status Filter */}
          <div className="flex gap-2">
            {['all', 'approved', 'pending', 'rejected'].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                  statusFilter === status
                    ? 'bg-personic-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {attestations.length === 0 ? (
        <div className="px-6 py-8 text-center text-gray-500">
          No attestations found for this tenant.
        </div>
      ) : (
        <div className="divide-y divide-gray-200">
          {attestations.map((attestation) => (
            <div key={attestation.id} className="px-6 py-4 hover:bg-gray-50">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-medium text-gray-900">
                      {attestation.regulated_domain}
                    </span>
                    <StatusBadge status={attestation.status} />
                  </div>

                  <div className="text-sm text-gray-600 space-y-1">
                    <div>Profile: {attestation.canonical_id}</div>
                    <div>Submitted by: {attestation.submitted_by}</div>
                    <div>Submitted: {new Date(attestation.submitted_at).toLocaleString()}</div>
                    {attestation.reviewed_at && (
                      <div>
                        Reviewed: {new Date(attestation.reviewed_at).toLocaleString()}
                        {attestation.reviewer_name && ` by ${attestation.reviewer_name}`}
                      </div>
                    )}
                    {attestation.expiration_date && (
                      <div>Expires: {new Date(attestation.expiration_date).toLocaleDateString()}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    expired: 'bg-gray-100 text-gray-800',
  }

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
        styles[status] || styles.pending
      }`}
    >
      {status}
    </span>
  )
}
